import { z } from 'zod';
// Knowledge base files. Every change makes a new version of the knowledge base, and agents stay on the version they
// were given until someone points them at a newer one, so each change reports the new version.
const MAX_FILE_CHARS = 20_000;
/** Names the docs recommend: one question per file, such as QHowDoIMakeAPayment.md. */
export const KNOWLEDGE_FILE_NAME_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{0,120}\.md$/;
const versionSchema = z
    .string()
    .describe('The knowledge base version this change created; agents only use it once pointed at it');
export const listKnowledgeFilesToolSchema = {
    name: 'list_knowledge_files',
    description: "List the files in a knowledge base's latest version.",
    inputSchema: z.object({ knowledgeBaseId: z.string() }),
    outputSchema: z.object({
        knowledgeBaseId: z.string(),
        versionId: z.string().optional(),
        files: z.array(z.object({
            fileId: z.string(),
            name: z.string(),
            fromWebsite: z.boolean().describe('Crawled from a website; the Sync button on the file re-reads it')
        }))
    })
};
export const readKnowledgeFileToolSchema = {
    name: 'read_knowledge_file',
    description: "Read a knowledge base file's text.",
    inputSchema: z.object({ knowledgeBaseId: z.string(), fileId: z.string() }),
    outputSchema: z.object({
        fileId: z.string(),
        name: z.string(),
        text: z.string(),
        shortened: z.boolean().describe(`Only the first ${MAX_FILE_CHARS} characters are shown`)
    })
};
export const createKnowledgeBaseToolSchema = {
    name: 'create_knowledge_base',
    description: 'Create an empty knowledge base.',
    inputSchema: z.object({
        name: z
            .string()
            .min(1)
            .max(100)
            .regex(/^[A-Za-z0-9 ._-]+$/, 'Letters, numbers, spaces, dots, hyphens and underscores only')
    }),
    outputSchema: z.object({ knowledgeBaseId: z.string(), name: z.string() })
};
export const addKnowledgeFileToolSchema = {
    name: 'add_knowledge_file',
    description: 'Add a markdown file to a knowledge base. This makes a new version of the knowledge base; agents keep the old one until pointed at it.',
    inputSchema: z.object({
        knowledgeBaseId: z.string(),
        fileName: z
            .string()
            .regex(KNOWLEDGE_FILE_NAME_PATTERN)
            .describe('Such as QDoesMyPolicyCoverARentalCar.md; must not match an existing file'),
        text: z.string().min(1).max(MAX_FILE_CHARS)
    }),
    outputSchema: z.object({ knowledgeBaseId: z.string(), versionId: versionSchema, fileId: z.string() })
};
export const updateKnowledgeFileToolSchema = {
    name: 'update_knowledge_file',
    description: "Replace a knowledge base file's text. This makes a new version of the knowledge base, in which the file has a new id.",
    inputSchema: z.object({
        knowledgeBaseId: z.string(),
        fileId: z.string(),
        text: z.string().min(1).max(MAX_FILE_CHARS).describe('The whole new text, not just the change')
    }),
    outputSchema: z.object({ knowledgeBaseId: z.string(), versionId: versionSchema, fileId: z.string() })
};
export const removeKnowledgeFileToolSchema = {
    name: 'remove_knowledge_file',
    description: 'Remove a file from a knowledge base. This makes a new version without it; older versions keep it.',
    inputSchema: z.object({ knowledgeBaseId: z.string(), fileId: z.string() }),
    outputSchema: z.object({ knowledgeBaseId: z.string(), versionId: versionSchema, name: z.string() })
};
export const deleteKnowledgeBaseToolSchema = {
    name: 'delete_knowledge_base',
    description: "Delete a knowledge base. It's refused while a published agent uses it, and it's removed from agents' unpublished drafts.",
    inputSchema: z.object({ knowledgeBaseId: z.string() }),
    outputSchema: z.object({ knowledgeBaseId: z.string(), name: z.string() })
};
/** The file name, from its storage path. */
export const knowledgeFileName = ({ filePath }) => filePath.split('/').at(-1) ?? filePath;
export const describeKnowledgeFiles = (files) => files.map((file) => ({
    fileId: file.fileId,
    name: knowledgeFileName(file),
    fromWebsite: Boolean(file.ingestionUrl)
}));
export const describeKnowledgeFileText = (fileId, name, text) => ({
    fileId,
    name,
    text: text.slice(0, MAX_FILE_CHARS),
    shortened: text.length > MAX_FILE_CHARS
});
//# sourceMappingURL=knowledge.js.map